import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Factory,
  Eye,
  Newspaper,
  Settings,
  Plus,
  RefreshCw,
  Shield,
  Search,
} from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppData } from '../../contexts/AppDataContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../UI/dialog';
import { Input } from '../UI/input';
import { cn } from '../../lib/utils';

const NAV_ITEMS = [
  { id: 'dashboard', href: '/dashboard', nameKey: 'dashboardTitle', icon: LayoutDashboard },
  { id: 'companies', href: '/companies', nameKey: 'companiesTitle', icon: Building2 },
  { id: 'my-companies', href: '/my-companies', nameKey: 'myCompaniesTitle', icon: Briefcase },
  { id: 'my-companies-t1', href: '/my-companies?tier=1', nameKey: 'commandPaletteTier1', icon: Briefcase },
  { id: 'my-companies-t2', href: '/my-companies?tier=2', nameKey: 'commandPaletteTier2', icon: Briefcase },
  { id: 'my-companies-t3', href: '/my-companies?tier=3', nameKey: 'commandPaletteTier3', icon: Briefcase },
  { id: 'tenant', href: '/my-tenant-company', nameKey: 'myTenantCompanyTitle', icon: Factory },
  { id: 'watchlist', href: '/watchlist', nameKey: 'watchlistTitle', icon: Eye },
  { id: 'news', href: '/news', nameKey: 'newsTitle', icon: Newspaper },
  { id: 'settings', href: '/settings', nameKey: 'settingsTitle', icon: Settings },
];

const ACTION_ITEMS = [
  { id: 'add-supplier', href: '/my-companies', nameKey: 'commandPaletteAddSupplier', icon: Plus },
  { id: 'risk-companies', href: '/companies', nameKey: 'commandPaletteRiskAnalysis', icon: Shield },
  { id: 'refresh', action: 'refresh', nameKey: 'commandPaletteRefreshData', icon: RefreshCw },
];

function CommandPalette({ open, onOpenChange }) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { companies, watchlist, refreshAll } = useAppData();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const runAction = useCallback(
    async (item) => {
      onOpenChange(false);
      if (item.action === 'refresh') {
        await refreshAll();
        return;
      }
      if (item.href) navigate(item.href);
    },
    [navigate, onOpenChange, refreshAll]
  );

  const companyResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const fromWatchlist = (watchlist || []).map((item) => {
      const c = item.company || {};
      const ticker = item.companyId ?? item.company_id ?? c.ticker ?? '';
      const name = c.name ?? ticker;
      return { ticker, name, href: ticker ? `/companies/${encodeURIComponent(ticker)}` : null };
    });
    const fromCompanies = (companies || []).map((c) => ({
      ticker: c.ticker,
      name: c.name,
      href: c.ticker ? `/companies/${encodeURIComponent(c.ticker)}` : null,
    }));
    const seen = new Set();
    const merged = [];
    [...fromWatchlist, ...fromCompanies].forEach((row) => {
      const key = (row.ticker || row.name || '').toLowerCase();
      if (!key || seen.has(key)) return;
      if (
        !q ||
        key.includes(q) ||
        String(row.name || '').toLowerCase().includes(q)
      ) {
        seen.add(key);
        if (row.href) merged.push(row);
      }
    });
    return merged.slice(0, 8);
  }, [query, watchlist, companies]);

  const filterByQuery = useCallback(
    (items) => {
      const q = query.trim().toLowerCase();
      if (!q) return items;
      return items.filter((item) => t(item.nameKey).toLowerCase().includes(q));
    },
    [query, t]
  );

  const navFiltered = filterByQuery(NAV_ITEMS);
  const actionsFiltered = filterByQuery(ACTION_ITEMS);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && companyResults.length > 0) {
      e.preventDefault();
      onOpenChange(false);
      navigate(companyResults[0].href);
    }
  };

  const renderItem = (item) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        type="button"
        className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-primary-50"
        onClick={() => runAction(item)}
      >
        <Icon className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden />
        <span className="truncate">{t(item.nameKey)}</span>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0 sm:max-w-xl"
        closeLabel={t('commandPaletteClose')}
      >
        <DialogHeader className="border-b border-border-light px-4 py-3">
          <DialogTitle className="sr-only">{t('commandPaletteTitle')}</DialogTitle>
          <div className="relative pr-8">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('commandPaletteSearchPlaceholder')}
              className="h-10 pl-9"
              autoFocus
            />
          </div>
          <p className="mt-2 text-xs text-text-tertiary">{t('commandPaletteHint')}</p>
        </DialogHeader>
        <div className="max-h-[min(60vh,400px)] overflow-y-auto px-2 py-2">
          {navFiltered.length > 0 && (
            <div className="mb-2">
              <p className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">
                {t('commandPaletteNavigation')}
              </p>
              {navFiltered.map(renderItem)}
            </div>
          )}
          {actionsFiltered.length > 0 && (
            <div className="mb-2">
              <p className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">
                {t('commandPaletteActions')}
              </p>
              {actionsFiltered.map(renderItem)}
            </div>
          )}
          {companyResults.length > 0 && (
            <div>
              <p className="px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-text-tertiary">
                {t('commandPaletteCompanies')}
              </p>
              {companyResults.map((row) => (
                <button
                  key={row.ticker}
                  type="button"
                  className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-primary-50"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(row.href);
                  }}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-text-secondary" aria-hidden />
                  <span className="truncate">
                    {row.name}
                    {row.ticker ? (
                      <span className="ml-2 text-text-tertiary">{row.ticker}</span>
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          )}
          {navFiltered.length === 0 &&
            actionsFiltered.length === 0 &&
            companyResults.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-text-tertiary">
                {t('commandPaletteNoResults')}
              </p>
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return { open, setOpen };
}

export function CommandPaletteTrigger({ onClick, className }) {
  const { t } = useLocale();
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-sm border border-border-light bg-background-secondary px-3 text-sm text-text-tertiary transition-colors hover:border-primary-300 hover:bg-primary-50/50',
        className
      )}
      aria-label={t('commandPaletteOpen')}
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1 truncate text-left">{t('commandPaletteSearchPlaceholder')}</span>
      <kbd className="hidden shrink-0 rounded border border-border-light bg-canvas-soft px-1.5 py-0.5 text-xs font-medium text-text-tertiary sm:inline">
        {isMac ? '⌘K' : 'Ctrl+K'}
      </kbd>
    </button>
  );
}

export default CommandPalette;
